import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Field, Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { DatePickerField } from "../../FormFields";
import {
  IPlace,
  ISchedule,
  IService,
  IUser,
  TypeOfDays,
  checkIfChosenServicePossibleAtTime,
  getTime,
  getTimeNumbersInArray,
  isRequired,
  placeQueryData,
  servicePaginationData,
  userRoles,
} from "../../../shared";
import {
  useAppSelector,
  useVisitListByServiceAndWorkerMap,
} from "../../../hooks";
import styles from "./visitForm.module.scss";
import {
  placeApi,
  scheduleApi,
  serviceApi,
  visitApi,
} from "../../../store/services";
import { SelectFieldWithError } from "../../FormFields/SelectFieldWithError";

interface IVisitInitialValues {
  serviceId?: string;
  placeId?: string;
  doctorId?: string;
  startTime?: string;
}

export interface IVisitForm {
  onSubmit: (values: any, form: any) => Promise<void>;
  initialValues?: IVisitInitialValues;
  visitId?: string;
  isVisitFromCalendar?: boolean;
  selectedPatient?: IUser;
}

export const VisitFormComponent: FC<IVisitForm> = ({
  onSubmit,
  initialValues,
  visitId,
  isVisitFromCalendar,
}) => {
  const { t } = useTranslation();

  const currentUser = useAppSelector(
    ({ userReducer }) => userReducer.currentUser,
  );

  const isPatient = currentUser?.role === userRoles.patient;

  const [formState, setFormState] = useState<{
    chosenClinic?: IPlace;
    chosenDate?: Date;
    chosenDoctor?: IUser;
    chosenService?: IService;
    filteredServices?: IService[];
  }>({});

  const {
    chosenClinic,
    chosenDate,
    chosenDoctor,
    chosenService,
    filteredServices,
  } = formState;

  const timeInterval = useMemo(
    () => (chosenService?.isRelatedToExercise ? 60 : 15),
    [chosenService?.isRelatedToExercise],
  );

  const { data: fetchedHospitalList } =
    placeApi.useFetchPlaceListQuery(placeQueryData);
  const { data: fetchedServiceList } = serviceApi.useFetchServiceListQuery({
    ...servicePaginationData,
    limit: 100,
  });

  const visitQueryParams = useMemo(
    () => ({
      from: chosenDate
        ? moment(chosenDate).startOf("day").toISOString(true)
        : "",
      to: chosenDate ? moment(chosenDate).endOf("day").toISOString(true) : "",
      placeId: chosenClinic?._id || "",
    }),
    [chosenDate, chosenClinic?._id],
  );

  const { data: fetchedVisitList } = visitApi.useFetchPublicVisitListQuery(
    visitQueryParams,
    {
      skip: !chosenDate || !chosenClinic?._id,
      refetchOnMountOrArgChange: true,
    },
  );

  const { data: fetchedSchedule } = scheduleApi.useFetchUserScheduleQuery(
    visitQueryParams,
    {
      refetchOnMountOrArgChange: true,
      skip: !chosenDate || !chosenClinic?._id,
    },
  );

  const serviceList = useMemo(
    () => fetchedServiceList?.items || [],
    [fetchedServiceList?.items],
  );

  const hospitalList = useMemo(
    () => fetchedHospitalList?.items || [],
    [fetchedHospitalList?.items],
  );

  const visits = useMemo(
    () => fetchedVisitList?.items || [],
    [fetchedVisitList?.items],
  );

  const workerListByScheduleAndService = useMemo(() => {
    if (!fetchedSchedule?.length || !chosenService?._id) {
      return [];
    }
    const isRehabilitatorRequired = chosenService?.isRelatedToExercise;

    return fetchedSchedule.filter((schedule) => {
      const hasService = schedule.user?.portfolio?.serviceIds?.includes(
        chosenService._id,
      );

      const isRoleValid = isRehabilitatorRequired
        ? schedule.user?.role === userRoles.rehabilitator
        : true;

      return hasService && isRoleValid;
    });
  }, [fetchedSchedule, chosenService?._id, chosenService?.isRelatedToExercise]);

  const visitListByServiceAndWorkerMap = useVisitListByServiceAndWorkerMap({
    visits,
    workerListByScheduleAndService,
    chosenService,
    timeInterval,
  });

  const scheduleTimeData = useMemo(() => {
    if (!workerListByScheduleAndService?.length) {
      return { openAt: undefined, closeAt: undefined };
    }

    const startTimes = workerListByScheduleAndService
      .map((worker: ISchedule) => worker?.startAt as string)
      .filter(Boolean)
      .sort((a, b) => moment(a).unix() - moment(b).unix());

    const endTimes = workerListByScheduleAndService
      .map((worker) => worker?.finishAt as string)
      .filter(Boolean)
      .sort((a, b) => moment(b).unix() - moment(a).unix());

    return {
      openAt: getTimeNumbersInArray(startTimes[0]),
      closeAt: getTimeNumbersInArray(endTimes[0]),
    };
  }, [workerListByScheduleAndService]);

  const workerListByTime = useMemo(() => {
    if (!fetchedSchedule?.length || !chosenDate) {
      return [];
    }

    const relatedScheduleList = fetchedSchedule.filter(
      (schedule) =>
        moment(chosenDate).isSameOrAfter(schedule.startAt) &&
        moment(chosenDate).isBefore(schedule.finishAt),
    );

    const filteredWorkerListByTime = workerListByScheduleAndService
      .filter((worker) =>
        relatedScheduleList.find(
          (schedule) => schedule.userId === worker.user?._id,
        ),
      )
      .map((worker) => worker.user as IUser);

    return filteredWorkerListByTime.filter(
      (worker) =>
        !visitListByServiceAndWorkerMap.visitList?.[
          worker._id
        ]?.bookedVisitTime?.find((time) =>
          moment(time).isSame(moment(chosenDate)),
        ),
    );
  }, [
    fetchedSchedule,
    chosenDate,
    workerListByScheduleAndService,
    visitListByServiceAndWorkerMap.visitList,
  ]);

  const handleFilteredDate = useCallback(
    (date: Date) => {
      const day = date?.getDay();
      return chosenService?.isRelatedToExercise
        ? !chosenClinic?.schedule[day as TypeOfDays].isHoliday
        : true;
    },
    [chosenService?.isRelatedToExercise, chosenClinic?.schedule],
  );

  const createHoursOfDayArray = useCallback(() => {
    if (!chosenDate) return [];

    const hoursArray = [];
    let startOfDay = moment(chosenDate).startOf("day").add(1, "hour");
    const endOfDay = moment(chosenDate).endOf("day");

    while (startOfDay <= endOfDay) {
      hoursArray.push(startOfDay.toDate());
      startOfDay.add(1, "hour");
    }

    return hoursArray;
  }, [chosenDate]);

  const memoizedExcludeTimes = useMemo(() => {
    if (!workerListByScheduleAndService?.length) {
      return createHoursOfDayArray();
    }
    return undefined;
  }, [workerListByScheduleAndService?.length, createHoursOfDayArray]);

  const handleServiceChange = useCallback(
    (value: string) => {
      const service = serviceList.find((s) => s._id === value);
      setFormState((prev) => ({
        ...prev,
        chosenService: service,
        chosenDate: undefined,
        chosenDoctor: undefined,
      }));
    },
    [serviceList],
  );

  const handleClinicChange = useCallback(
    (value: string) => {
      const clinic = hospitalList.find((c) => c._id === value);
      setFormState((prev) => ({
        ...prev,
        chosenClinic: clinic,
        chosenDate: undefined,
      }));
    },
    [hospitalList],
  );

  const handleDoctorChange = useCallback(
    (value: string) => {
      const doctor = workerListByTime.find((w) => w._id === value);
      setFormState((prev) => ({
        ...prev,
        chosenDoctor: doctor,
      }));
    },
    [workerListByTime],
  );

  const handleDateChange = useCallback((date: Date) => {
    setFormState((prev) => ({
      ...prev,
      chosenDate: date,
    }));
  }, []);

  const serviceOptions = useMemo(
    () =>
      (filteredServices || serviceList)
        .filter((service): service is IService & { _id: string } =>
          Boolean(service._id),
        )
        .map((service) => ({
          id: service._id,
          title: service.title,
        })),
    [filteredServices, serviceList],
  );

  const hospitalOptions = useMemo(
    () =>
      hospitalList
        .filter((clinic): clinic is IPlace & { _id: string } =>
          Boolean(clinic._id),
        )
        .map((clinic) => ({
          id: clinic._id,
          title: clinic.title,
        })),
    [hospitalList],
  );

  const workerOptions = useMemo(
    () =>
      workerListByTime
        .filter((doctor): doctor is IUser & { _id: string } =>
          Boolean(doctor._id),
        )
        .map((doctor) => ({
          id: doctor._id,
          title: doctor.firstName,
          label: `${doctor.firstName} ${doctor.lastName}`,
        })),
    [workerListByTime],
  );

  useEffect(() => {
    if (isVisitFromCalendar && chosenDoctor) {
      setFormState((prev) => ({
        ...prev,
        filteredServices: chosenDoctor.portfolio?.services,
      }));
    }
  }, [chosenDoctor, isVisitFromCalendar]);

  useEffect(() => {
    if (initialValues?.placeId && initialValues?.serviceId) {
      const foundService = serviceList.find(
        (service) => service._id === initialValues.serviceId,
      );
      const foundClinic = hospitalList.find(
        (clinic) => clinic._id === initialValues.placeId,
      );

      setFormState((prev) => ({
        ...prev,
        chosenService: foundService,
        chosenClinic: foundClinic,
        chosenDoctor: undefined,
        chosenDate: undefined,
      }));
    }
  }, [initialValues, serviceList, hospitalList]);

  const noAvailableWorkers =
    fetchedSchedule &&
    fetchedSchedule?.length > 0 &&
    workerListByTime?.length === 0 &&
    chosenDate &&
    chosenClinic &&
    chosenService;

  const noWorkersAtSchedule =
    fetchedSchedule?.length === 0 &&
    chosenService &&
    chosenDate &&
    chosenClinic;

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={visitId || isVisitFromCalendar ? initialValues : undefined}
      render={({ handleSubmit, form, submitting, submitFailed }) => (
        <form onSubmit={handleSubmit}>
          <SelectFieldWithError
            name="serviceId"
            label={t("adminPanel.form.serviceLabel")}
            placeholder={t("adminPanel.form.chooseService")}
            options={serviceOptions}
            submitFailed={submitFailed}
            onChange={(value) => {
              form.change("serviceId", value);
              handleServiceChange(value);
              form.change("doctorId", undefined);
            }}
            disabled={!!visitId}
          />

          <SelectFieldWithError
            name="placeId"
            label={t("adminPanel.form.clinicLabel")}
            placeholder={t("adminPanel.form.chooseClinic")}
            options={hospitalOptions}
            submitFailed={submitFailed}
            onChange={(value) => {
              form.change("placeId", value);
              handleClinicChange(value);
            }}
            disabled={!!visitId}
          />

          <div className={styles.date_wrap}>
            <div className={styles.date_picker_label_wrap}>
              <label>{t("adminPanel.form.dateLabel")}*</label>
              {!chosenService && chosenDate && (
                <span>{t("noChosenService")}</span>
              )}
              {workerListByTime?.length === 0 &&
                !chosenClinic &&
                chosenDate && <span>{t("noChosenClinic")}</span>}
              {noAvailableWorkers && <span>{t("noAvailableWorkers")}</span>}
              {noWorkersAtSchedule && <span>{t("noWorkersAtSchedule")}</span>}
              {checkIfChosenServicePossibleAtTime(
                chosenDate as Date,
                chosenService,
                visitListByServiceAndWorkerMap.generalBookedTime,
              ) && <span>{t("adminPanel.form.noAvailableTime")}</span>}
            </div>
            <Field
              name="startTime"
              validate={isRequired}
              component={DatePickerField}
              handleFilteredDate={handleFilteredDate}
              setChosenDate={handleDateChange}
              chosenDate={chosenDate}
              timeInterval={timeInterval}
              excludeTimes={
                Object.keys(
                  visitListByServiceAndWorkerMap.generalBookedTime || {},
                ).length
                  ? visitListByServiceAndWorkerMap.generalBookedTime
                  : memoizedExcludeTimes
              }
              excludeDates={[]}
              isHiddenTimes={
                !fetchedSchedule?.length ||
                !workerListByScheduleAndService?.length
              }
              minTime={getTime(
                scheduleTimeData.openAt?.[0],
                scheduleTimeData.openAt?.[1],
              )}
              maxTime={getTime(
                scheduleTimeData.closeAt?.[0]
                  ? scheduleTimeData.closeAt[0] - 1
                  : scheduleTimeData.closeAt?.[0],
                60 - (chosenService?.duration || 0),
              )}
            />
          </div>

          {chosenService?.isRelatedToExercise && workerListByTime?.length ? (
            <SelectFieldWithError
              name="doctorId"
              label={t("adminPanel.form.chooseWorkerLabel")}
              placeholder=""
              options={workerOptions.slice(0, 1)}
              disabled={true}
              defaultValue={workerListByTime[0]._id}
              submitFailed={submitFailed}
            />
          ) : (
            <SelectFieldWithError
              name="doctorId"
              label={t("adminPanel.form.chooseWorkerLabel")}
              placeholder={t("adminPanel.form.chooseWorker")}
              options={workerOptions}
              submitFailed={submitFailed}
              onChange={(value) => {
                form.change("doctorId", value);
                handleDoctorChange(value);
              }}
            />
          )}

          {isPatient && (
            <div className={styles.warning_notice_wrap}>
              <p className={styles.warning_notice}>{t("notBeRefunded1")}</p>
              <p className={styles.warning_notice}>{t("notBeRefunded2")}</p>
            </div>
          )}
          <button
            type="submit"
            className={styles.form_button}
            disabled={submitting}
          >
            {!visitId ? t("buttons.create") : t("buttons.save")}
          </button>
        </form>
      )}
    />
  );
};
